import { jsxs, jsx } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { c as classNames, l as liveAnnounce, L as Level, h as useTheme, T as ThemeContext } from '../theme-provider/index-chunk.esm.js';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';
import styles from '../styles/uitk-egds-sheet.css';
import { M as MakeEgdsSizingLength } from '../egds-sizing-length/index-chunk.esm.js';
import { CSSTransition } from 'react-transition-group';

const EGDSSheetPositionTypes = {
    LEADING: 'leading',
    TRAILING: 'trailing',
};
const EGDSSideSheetColCountsMap = [
    [1, '1'],
    [2, '2'],
    [3, '3'],
    [4, '4'],
    [6, '6'],
    [8, '8'],
    [12, '12'],
];
const EGDSSideSheetColCounts = new Map(EGDSSideSheetColCountsMap);

/* Collection of utilities used by Sheets */
let componentProps;
let sheetElem;
let currentIndex = -1;
const setFocus = (ref) => {
    if (ref && ref.current) {
        ref.current.focus();
    }
};
const setFocusableElems = (sheetRef) => {
    if (sheetRef?.current) {
        return Array.from(sheetRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
    }
    return [];
};
const setElementFocus = (sheetRef, hasModal, focusElemRef) => {
    const focusable = setFocusableElems(sheetRef);
    if (focusElemRef?.current) {
        focusElemRef.current.focus();
    }
    else if (focusable.length) {
        focusable[hasModal ? 1 : 0]?.focus();
    }
    else {
        setFocus(sheetRef);
    }
};
const trapUpperBoundFocus = (sheetRef) => {
    const focusable = setFocusableElems(sheetRef);
    if (focusable.length > 1) {
        focusable[focusable.length - 2].focus(); // Send focus to last element before boundary
    }
};
const trapLowerBoundFocus = (sheetRef) => {
    const focusable = setFocusableElems(sheetRef);
    if (focusable.length > 1) {
        focusable[1].focus(); // Send focus to first element after boundary
    }
};
const defineSheetLayout = (children) => {
    const childrenArray = React.Children.toArray(children);
    const sheetLayoutObj = {
        get hasCloseButton() {
            return !!this.closeButtonElem;
        },
        get hasAlignment() {
            return !!this.hasCloseButton && (this.closeButtonElem?.props?.alignment ?? 'right');
        },
        get layoutProps() {
            return (this.hasCloseButton &&
                {
                    space: 'two',
                    direction: this.hasAlignment === 'top' ? 'column' : 'row-reverse',
                });
        },
        get closeButtonLayoutProps() {
            return (this.hasCloseButton &&
                this.hasAlignment === 'top' &&
                {
                    alignSelf: 'end',
                });
        },
        closeButtonElem: childrenArray.find((child) => child.type?.displayName?.match(/(EGDS|Uitk)SheetCloseButton/)),
        footerElem: childrenArray.find((child) => child.type?.displayName?.match(/(EGDS|Uitk)SheetFooter/)),
        childrenElem: childrenArray.filter((child) => {
            if (child.type?.displayName) {
                // Regular expression to match "Uitk" or "EGDS" followed by "SheetCloseButton" or "SheetFooter"
                const sheetElemRegex = /^(EGDS|Uitk)(SheetCloseButton|SheetFooter)$/;
                return !sheetElemRegex.test(child.type.displayName);
            }
            return true; // This returns all the other elements
        }),
    };
    return sheetLayoutObj;
};
const isDescendant = (parent, child) => {
    /**
     * Checks if the clear button was clicked on the body.
     * This was added because there's an edge case while using pwa in which the button is not treated as a descendant of the sheet.
     * Making the sheet close when clicking the clear button.
     */
    const closestButton = child?.closest('button');
    const isClearButtonFallback = closestButton && closestButton?.className.includes('clear');
    return !!parent?.current?.contains(child) || isClearButtonFallback;
};
const onBodyClick = (e) => {
    const { disableBodyClickHandler, triggerRef, onDismiss } = componentProps;
    let { target } = e;
    /**
     * Checks if the sheet is within a shadow Dom and if so, sets the target to the composed path rather than the root.
     * This was added because there was an edge case where the sheet is within a shadow dom and would open and dismiss on the same click.
     */
    if (e.target.shadowRoot) {
        target = e.composedPath()[0];
    }
    if (triggerRef?.current === target ||
        disableBodyClickHandler ||
        isDescendant(sheetElem, target) ||
        isDescendant(triggerRef, target)) {
        return;
    }
    onDismiss && onDismiss();
};
const onKeyDown = (e) => {
    const { disableArrowKeyHandler, onDismiss } = componentProps;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (disableArrowKeyHandler) {
            return;
        }
        const target = document.activeElement;
        // Don't prevent default if the active element is a select, or number input
        if (target instanceof HTMLSelectElement || (target instanceof HTMLInputElement && target.type === 'number')) {
            return;
        }
        e.preventDefault();
    }
    if (e.key !== 'Escape') {
        return;
    }
    onDismiss && onDismiss();
};
const onKeyUp = (e) => {
    const focusable = setFocusableElems(sheetElem);
    const { disableArrowKeyHandler, onDismiss, triggerRef } = componentProps;
    const parent = sheetElem;
    let target;
    if (e.shiftKey && e.key === 'Tab') {
        target = document.activeElement;
        if (triggerRef?.current === e.target) {
            return false;
        }
    }
    if (parent.current) {
        setFocusableElems(sheetElem);
        if (e.key === 'ArrowDown') {
            if (disableArrowKeyHandler) {
                return;
            }
            target = document.activeElement;
            if (triggerRef?.current === e.target) {
                currentIndex = 0;
                focusable[currentIndex].focus();
                return;
            }
            const elemToFocusOn = focusable[currentIndex + 1];
            if (elemToFocusOn) {
                currentIndex += 1;
                elemToFocusOn.focus();
            }
        }
        else if (e.key === 'ArrowUp') {
            if (disableArrowKeyHandler) {
                return;
            }
            target = document.activeElement;
            if (triggerRef?.current === e.target) {
                return false;
            }
            const elemToFocusOn = focusable[currentIndex - 1];
            if (elemToFocusOn) {
                currentIndex -= 1;
                elemToFocusOn.focus();
            }
        }
        else if (e.key === 'Tab') {
            target = document.activeElement;
            /**
             * Checks if the component is within a shadow dom and if so, sets the target to the composed path rather than the root.
             * This was added because there was an edge case where the sheet used for the calendar is within a shadow dom and would open and dismiss while using keyboard navigation.
             */
            if (e.target.shadowRoot) {
                target = e.composedPath()[0];
            }
            for (let i = 0; i <= focusable.length; i++) {
                if (target === focusable[i]) {
                    currentIndex = i;
                }
            }
            const isElemDescendant = isDescendant(parent, target);
            if (!isElemDescendant) {
                onDismiss && onDismiss();
            }
        }
    }
};
const attachHandlers = (props, sheetRef) => {
    const { body } = document;
    componentProps = props;
    sheetElem = sheetRef;
    body.addEventListener('click', onBodyClick, { capture: true });
    body.addEventListener('keydown', onKeyDown);
    body.addEventListener('keyup', onKeyUp);
};
const detachHandlers = () => {
    const { body } = document;
    currentIndex = -1; // Resets the currentIndex back to -1 upon closing
    body.removeEventListener('click', onBodyClick, { capture: true });
    body.removeEventListener('keydown', onKeyDown);
    body.removeEventListener('keyup', onKeyUp);
};
const isValidEGDSSheetPosition = (value) => Object.values(EGDSSheetPositionTypes).includes(value);
/**
 * Add or remove the "has sheet" class name to the body element
 *
 * @param action 'add' | 'remove'
 * @returns target.add(className) || target.remove(className)
 */
const hasSheetClassName = (action) => {
    const className = 'has-uitk-sheet';
    const target = document.body.classList;
    return action === 'add' ? target.add(className) : target.remove(className);
};

const getClassNameList$9 = classNames.bind(styles);
const EGDSBottomSheet = (props) => {
    const { ariaLabel, autoFocus = true, children, className, focusElemRef, fullWidth = false, hasModal, height, isExpanded, isVisible, onDismiss, returnFocusOnClose = true, triggerRef, type = 'bottom', ...attributes } = props;
    const sheetRef = React.useRef(null);
    const baseClassName = `uitk-${type}-sheet`;
    let initialTouchY;
    let finalTouchY;
    const handleTouchStart = (e) => {
        initialTouchY = e.targetTouches[0].clientY;
    };
    const handleTouchEnd = (e) => {
        finalTouchY = e.changedTouches[0].clientY;
        const deltaY = finalTouchY - initialTouchY;
        const swipeThreshold = 50;
        if (deltaY > swipeThreshold && e.target.scrollTop === 0) {
            onDismiss && onDismiss();
        }
    };
    const sheetClassNames = getClassNameList$9(baseClassName, {
        [`${baseClassName}-full-width`]: fullWidth,
        [`${baseClassName}-expanded`]: isExpanded,
    }, className);
    const sheetStyles = {
        ...(height && {
            [`--uitk-bottom-sheet-height`]: `${MakeEgdsSizingLength(height)}`,
        }),
    };
    // Set focus when mount and/or update
    React.useEffect(() => {
        if (isVisible) {
            attachHandlers(props, sheetRef);
            // Reset liveannounce so the liveannounce element is not navigable by screenreader.
            liveAnnounce('', Level.POLITE);
            if (autoFocus) {
                setElementFocus(sheetRef, hasModal, focusElemRef);
            }
        }
        hasModal && hasSheetClassName('add');
    }, [autoFocus, isVisible, hasModal, isExpanded]);
    // "unmount" hook
    React.useEffect(() => () => {
        if (!isVisible) {
            detachHandlers();
        }
        if (returnFocusOnClose) {
            setFocus(triggerRef);
        }
        hasModal && hasSheetClassName('remove');
    }, [hasModal, isVisible, returnFocusOnClose, triggerRef]);
    const { hasCloseButton, layoutProps, closeButtonLayoutProps, closeButtonElem, childrenElem } = defineSheetLayout(children);
    const l10n = useLocalization();
    return (jsxs("section", { onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, ref: sheetRef, className: sheetClassNames, tabIndex: -1, role: "dialog", "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'), style: sheetStyles, ...attributes, children: [hasModal && jsx("div", { tabIndex: 0, onFocus: trapUpperBoundFocus, "aria-hidden": "true" }), hasCloseButton && (jsxs(EGDSLayoutFlex, { ...layoutProps, children: [jsx(EGDSLayoutFlexItem, { ...closeButtonLayoutProps, children: jsx("div", { children: closeButtonElem }) }), childrenElem && jsx(EGDSLayoutFlexItem, { children: childrenElem })] })), !hasCloseButton && childrenElem, hasModal && jsx("div", { tabIndex: 0, onFocus: trapLowerBoundFocus, "aria-hidden": "true" })] }));
};
EGDSBottomSheet.displayName = 'EGDSBottomSheet';

const EGDSSheetContext = React.createContext({
    maxHeight: undefined,
});

const getClassNameList$8 = classNames.bind(styles);
const EGDSPopoverSheet = (props) => {
    const { ariaLabel, autoFocus = true, autoPosition = true, children, className, fullWidth = false, hasModal, intersectionObserverRoot, isVisible, maxHeight, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onDismiss, position: passedInPosition = { block: EGDSSheetPositionTypes.TRAILING, inline: EGDSSheetPositionTypes.LEADING }, applyBlockStartMargin = false, showOverTrigger = false, returnFocusOnClose = true, triggerRef, focusElemRef, type = 'popover', width, ...attributes } = props;
    const blockPosition = passedInPosition.block && isValidEGDSSheetPosition(passedInPosition.block)
        ? passedInPosition.block
        : EGDSSheetPositionTypes.TRAILING;
    const inlinePosition = passedInPosition.inline && isValidEGDSSheetPosition(passedInPosition.inline)
        ? passedInPosition.inline
        : EGDSSheetPositionTypes.LEADING;
    // State hooks
    const [offset, setOffset] = React.useState({
        x: 0,
        y: 0,
    });
    const [animationHasEnded, setAnimationHasEnded] = React.useState(false);
    const prevOffsetStoreRef = React.useRef({ x: 0, y: 0 });
    const sheetRef = React.useRef(null);
    const intersectionObserverRootRef = React.useRef(null);
    const baseClassName = `uitk-${type}-sheet`;
    const sheetClassNames = getClassNameList$8(baseClassName, className, `${baseClassName}-inline-pos-${inlinePosition}`, `${baseClassName}-block-pos-${blockPosition}`, {
        [`${baseClassName}-full-width`]: fullWidth,
    }, {
        [`${baseClassName}-open`]: isVisible,
    }, {
        [`${baseClassName}-has-intersection-root-el`]: autoPosition && intersectionObserverRoot,
    }, {
        [`${baseClassName}-has-block-start-margin`]: blockPosition === EGDSSheetPositionTypes.TRAILING && applyBlockStartMargin,
    });
    let intersectionObserver;
    const unobserveIntersectionObserver = React.useCallback(() => {
        if (autoPosition && sheetRef.current) {
            intersectionObserver && intersectionObserver.unobserve(sheetRef.current);
        }
    }, [autoPosition, intersectionObserver]);
    const onAnimationEndCallback = (e) => {
        // There are multiple animationEnd callbacks due to there being 2-3 animations applied, this logic ensures animationEndedStateCallback() is only called once.
        !animationHasEnded && e.animationName.match(/sheet-grow-/) && setAnimationHasEnded(true);
    };
    // This callback may appear to be doing something similar to adjustAlignment(), but it's actually only required for Safari desktop.
    // Safari desktop doesn't currently support the use of css variables inside keyframe animations. Therefore the updates to --menu-grow-x and --menu-grow-y have no impact in safari desktop.
    // In order to get the auto positioning working in safari desktop, we need to use transform: translate and set css variables for x and y to be used in the translate.
    // There's also a corresponding CSS "hack" to target safari desktop only ln 59 in uitk-menu.scss
    // The reason all of this is done once the animation is complete, is because if we do this during the animation, it causes some jankiness.
    const animationEndedStateCallback = React.useCallback(() => {
        // eslint-disable-next-line no-negated-condition
        const viewport = typeof document !== 'undefined' ? document.documentElement : null;
        if (sheetRef?.current && viewport) {
            const container = sheetRef.current;
            const windowInnerHeight = window.innerHeight;
            const containerBoundingClientRect = container.getBoundingClientRect();
            const viewportBoundingClientRect = viewport.getBoundingClientRect();
            let newOffsetX = 0;
            let newOffsetY = 0;
            if (containerBoundingClientRect.right > viewportBoundingClientRect.right) {
                newOffsetX = -Math.abs(Math.ceil(containerBoundingClientRect.right) - Math.ceil(viewportBoundingClientRect.right));
            }
            else if (containerBoundingClientRect.left < viewportBoundingClientRect.left) {
                newOffsetX = Math.abs(containerBoundingClientRect.left);
            }
            if (blockPosition === EGDSSheetPositionTypes.TRAILING && containerBoundingClientRect.bottom > windowInnerHeight) {
                newOffsetY = -Math.abs(Math.ceil(containerBoundingClientRect.bottom) - Math.ceil(windowInnerHeight));
            }
            else if (blockPosition === EGDSSheetPositionTypes.LEADING && containerBoundingClientRect.top < 0) {
                newOffsetY = Math.abs(Math.ceil(containerBoundingClientRect.top));
            }
            if (newOffsetX || newOffsetY) {
                container.style.setProperty('--uitk-popover-sheet-offset-translate-x', `${newOffsetX}px`);
                container.style.setProperty('--uitk-popover-sheet-offset-translate-y', `${newOffsetY}px`);
            }
            unobserveIntersectionObserver(); // Unobserve after the initial re-aligment, so that the popover sheet doesn't end up far away from the trigger on scroll
        }
    }, [blockPosition, unobserveIntersectionObserver]);
    const adjustAlignment = (entry) => {
        const prevOffset = prevOffsetStoreRef.current;
        const isIntersectingOnRight = Math.ceil(entry.boundingClientRect.right) > Math.ceil(entry.intersectionRect.right);
        const isIntersectingOnLeft = Math.ceil(entry.boundingClientRect.left) < Math.ceil(entry.intersectionRect.left);
        const isIntersectingOnBottom = Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom);
        const isIntersectingOnTop = Math.ceil(entry.boundingClientRect.top) < Math.ceil(entry.intersectionRect.top);
        if (isIntersectingOnRight) {
            const currentOffsetX = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
            setOffset({
                x: -Math.abs(prevOffset.x - currentOffsetX),
                y: prevOffset.y,
            });
        }
        else if (isIntersectingOnLeft) {
            const currentOffsetX = entry.boundingClientRect.left - entry.intersectionRect.left;
            setOffset({
                x: prevOffset.x - currentOffsetX,
                y: prevOffset.y,
            });
        }
        if (isIntersectingOnBottom || isIntersectingOnTop) {
            const currentOffsetXRight = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
            const currentOffsetXLeft = entry.boundingClientRect.left - entry.intersectionRect.left;
            let offsetX = prevOffset.x;
            // If it is also intersecting on left or right, we need to adjust offset on X axis too
            if (isIntersectingOnLeft) {
                offsetX = prevOffset.x - currentOffsetXLeft;
            }
            else if (isIntersectingOnRight) {
                offsetX = -Math.abs(prevOffset.x - currentOffsetXRight);
            }
            if (blockPosition === EGDSSheetPositionTypes.TRAILING && isIntersectingOnBottom) {
                const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
                setOffset({
                    x: offsetX,
                    y: -Math.abs(prevOffset.y - currentOffsetY),
                });
            }
            else if (blockPosition === EGDSSheetPositionTypes.LEADING && isIntersectingOnTop) {
                const currentOffsetY = Math.ceil(entry.boundingClientRect.top) - Math.ceil(entry.intersectionRect.top);
                setOffset({
                    x: offsetX,
                    y: prevOffset.y - currentOffsetY,
                });
            }
        }
    };
    const autoPositionCallback = (entries) => {
        entries.forEach((entry) => {
            if (entry.intersectionRatio < 0.99 && entry.intersectionRatio > 0) {
                adjustAlignment(entry);
            }
        });
    };
    const autoPositionObserver = React.useCallback(() => {
        if (!(window && window.IntersectionObserver)) {
            return false;
        }
        if (intersectionObserverRoot) {
            intersectionObserverRootRef.current = document.querySelector(intersectionObserverRoot);
        }
        const thresholdSet = [];
        for (let i = 0; i <= 1.0; i += 0.01) {
            thresholdSet.push(i);
        }
        const defaultIntersectionObserverOptions = {
            threshold: thresholdSet,
        };
        const intersectionObserverOptions = intersectionObserverRoot
            ? Object.assign(defaultIntersectionObserverOptions, {
                root: intersectionObserverRootRef.current,
            })
            : defaultIntersectionObserverOptions;
        if (!intersectionObserver) {
            intersectionObserver = new IntersectionObserver(autoPositionCallback, intersectionObserverOptions);
        }
        sheetRef.current && intersectionObserver.observe(sheetRef.current);
    }, [intersectionObserverRoot]);
    // Hooks
    React.useEffect(() => {
        if (isVisible) {
            attachHandlers(props, sheetRef);
            // Reset liveannounce so the liveannounce element is not navigable by screenreader.
            liveAnnounce('', Level.POLITE);
            autoPosition && autoPositionObserver();
        }
    }, [autoFocus, autoPosition, autoPositionObserver, isVisible, hasModal, sheetRef, props]);
    // On mount
    React.useEffect(() => {
        // Set focus
        if (autoFocus && isVisible) {
            setElementFocus(sheetRef, false, focusElemRef);
        }
    }, [autoFocus, isVisible]);
    React.useEffect(() => {
        prevOffsetStoreRef.current = offset;
    }, [offset]);
    React.useEffect(() => {
        animationHasEnded && animationEndedStateCallback();
    }, [animationHasEnded, animationEndedStateCallback]);
    React.useEffect(() => {
        document.addEventListener('scroll', unobserveIntersectionObserver);
        // Clean-up
        return () => {
            document.removeEventListener('scroll', unobserveIntersectionObserver);
        };
    }, [unobserveIntersectionObserver]);
    // "unmount" hook
    React.useEffect(() => {
        if (!isVisible) {
            document.removeEventListener('scroll', unobserveIntersectionObserver);
            detachHandlers();
            unobserveIntersectionObserver();
            setOffset({ x: 0, y: 0 });
            setAnimationHasEnded(false);
            sheetRef?.current?.style.removeProperty('--uitk-popover-sheet-offset-translate-x');
            sheetRef?.current?.style.removeProperty('--uitk-popover-sheet-offset-translate-y');
            if (returnFocusOnClose) {
                setFocus(triggerRef);
            }
        }
    }, [isVisible, unobserveIntersectionObserver, returnFocusOnClose, triggerRef]);
    const { hasCloseButton, layoutProps, closeButtonLayoutProps, closeButtonElem, childrenElem } = defineSheetLayout(children);
    const posStyle = {
        ...(triggerRef?.current &&
            triggerRef.current.offsetParent &&
            sheetRef?.current && {
            [`--uitk-popover-sheet-block-pos`]: blockPosition === EGDSSheetPositionTypes.LEADING
                ? `${triggerRef.current.offsetTop -
                    (sheetRef.current.offsetHeight - (showOverTrigger ? triggerRef.current.offsetHeight : 0))}px`
                : `${triggerRef.current.offsetTop + (showOverTrigger ? 0 : triggerRef.current.offsetHeight)}px`,
            ...(inlinePosition === EGDSSheetPositionTypes.LEADING && {
                [`--uitk-popover-sheet-inline-pos-ltr`]: `${triggerRef.current.offsetLeft}px`,
                [`--uitk-popover-sheet-inline-pos-rtl`]: `${triggerRef.current.offsetParent.offsetWidth - triggerRef.current.offsetWidth - triggerRef.current.offsetLeft}px`,
            }),
            ...(inlinePosition === EGDSSheetPositionTypes.TRAILING && {
                [`--uitk-popover-sheet-inline-pos-ltr`]: `${triggerRef.current.offsetLeft + triggerRef.current.offsetWidth - sheetRef.current.offsetWidth}px`,
                [`--uitk-popover-sheet-inline-pos-rtl`]: `${triggerRef.current.offsetParent.offsetWidth -
                    triggerRef.current.offsetWidth -
                    triggerRef.current.offsetLeft -
                    (sheetRef.current.offsetWidth - triggerRef.current.offsetWidth)}px`,
            }),
        }),
        ...(width && {
            inlineSize: `${MakeEgdsSizingLength(width)}`,
        }),
        ...(offset && {
            '--uitk-popover-sheet-grow-x': `${offset.x}px`,
            '--uitk-popover-sheet-grow-y': `${offset.y}px`,
        }),
        ...(autoPosition &&
            intersectionObserverRootRef.current && {
            '--uitk-popover-sheet-max-inline-size': `${intersectionObserverRootRef.current.clientWidth}px`,
        }),
    };
    const sheetContextValue = React.useMemo(() => ({ maxHeight }), [maxHeight]);
    const l10n = useLocalization();
    return (jsx(EGDSSheetContext.Provider, { value: sheetContextValue, children: jsxs("section", { "data-testid": "popover-sheet", ref: sheetRef, className: sheetClassNames, style: posStyle, tabIndex: -1, role: "dialog", "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'), onAnimationEnd: onAnimationEndCallback, ...attributes, children: [hasCloseButton && (jsxs(EGDSLayoutFlex, { ...layoutProps, children: [jsx(EGDSLayoutFlexItem, { ...closeButtonLayoutProps, children: jsx("div", { children: closeButtonElem }) }), childrenElem && jsx(EGDSLayoutFlexItem, { children: childrenElem })] })), !hasCloseButton && childrenElem] }) }));
};
EGDSPopoverSheet.displayName = 'EGDSPopoverSheet';

const getClassNameList$7 = classNames.bind(styles);
const EGDSSideSheet = (props) => {
    const { ariaLabel, autoFocus = true, children, className, hasModal, isVisible, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onDismiss, overflow, returnFocusOnClose = true, sideSheetLayout, triggerRef, focusElemRef, type = 'side', ...attributes } = props;
    const sheetRef = React.useRef(null);
    const baseClassName = `uitk-${type}-sheet`;
    // Hooks
    // Set focus when initial mount only
    React.useEffect(() => {
        if (isVisible) {
            attachHandlers(props, sheetRef);
            // Reset liveannounce so the liveannounce element is not navigable by screenreader.
            liveAnnounce('', Level.POLITE);
            if (autoFocus) {
                setElementFocus(sheetRef, hasModal, focusElemRef);
            }
        }
        hasModal && hasSheetClassName('add');
    }, [autoFocus, hasModal, isVisible]);
    // "unmount" hook
    React.useEffect(() => () => {
        if (!isVisible) {
            detachHandlers();
        }
        if (returnFocusOnClose) {
            setFocus(triggerRef);
        }
        hasModal && hasSheetClassName('remove');
    }, [returnFocusOnClose, triggerRef]);
    const { hasCloseButton, layoutProps, closeButtonLayoutProps, closeButtonElem, childrenElem, footerElem } = defineSheetLayout(children);
    const layoutClasses = (obj, cssBaseClass) => {
        const layoutObj = obj;
        if (!('position' in obj)) {
            layoutObj.position = 'trailing'; // Set 'trailing' default
        }
        const str = Object.entries(layoutObj)
            .map(([key, val]) => `${cssBaseClass}-${key}-${val}`)
            .join(' ');
        return str;
    };
    const sheetClassNames = getClassNameList$7(baseClassName, sideSheetLayout && layoutClasses(sideSheetLayout, baseClassName), {
        [`${baseClassName}-position-trailing`]: !sideSheetLayout,
        [`${baseClassName}-has-overflow`]: overflow,
    }, className);
    const l10n = useLocalization();
    return (jsxs("section", { ref: sheetRef, className: sheetClassNames, tabIndex: -1, role: "dialog", "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'), ...attributes, children: [hasModal && jsx("div", { tabIndex: 0, onFocus: trapUpperBoundFocus }), hasCloseButton && (jsxs(EGDSLayoutFlex, { ...layoutProps, children: [jsx(EGDSLayoutFlexItem, { ...closeButtonLayoutProps, children: closeButtonElem }), jsx(EGDSLayoutFlexItem, { children: childrenElem })] })), !hasCloseButton && childrenElem, footerElem, hasModal && jsx("div", { tabIndex: 0, onFocus: trapLowerBoundFocus })] }));
};
EGDSSideSheet.displayName = 'EGDSSideSheet';

const getClassNameList$6 = classNames.bind(styles);
const EGDSCenteredSheet = (props) => {
    const { ariaLabel, autoFocus = true, children, className, isVisible, centeredSheetSize = 'medium', height, returnFocusOnClose = true, triggerRef, focusElemRef, type = 'centered', ariaModal = true, ...attributes } = props;
    const sheetRef = React.useRef(null);
    const baseClassName = `uitk-${type}-sheet`;
    const sheetClassNames = getClassNameList$6(baseClassName, className, centeredSheetSize === 'small' ? `uitk-centered-sheet-small` : 'uitk-centered-sheet-medium');
    const sheetStyles = {
        ...(height && {
            [`--uitk-centered-sheet-height`]: `${MakeEgdsSizingLength(height)}`,
        }),
    };
    // Hooks
    React.useEffect(() => {
        setFocusableElems(sheetRef);
    }, []);
    // Set focus when initial mount only
    React.useEffect(() => {
        if (autoFocus && isVisible) {
            setElementFocus(sheetRef, true, focusElemRef);
        }
        // Reset liveannounce so the liveannounce element is not navigable by screenreader.
        isVisible && liveAnnounce('', Level.POLITE);
        isVisible && hasSheetClassName('add');
    }, [autoFocus, isVisible]);
    // "unmount" hook
    React.useEffect(() => () => {
        if (returnFocusOnClose) {
            setFocus(triggerRef);
        }
        hasSheetClassName('remove');
    }, [returnFocusOnClose, triggerRef]);
    const trapUpperFocus = () => {
        setFocusableElems(sheetRef);
        trapUpperBoundFocus(sheetRef);
    };
    const trapLowerFocus = () => {
        setFocusableElems(sheetRef);
        trapLowerBoundFocus(sheetRef);
    };
    const { hasCloseButton, layoutProps, closeButtonLayoutProps, closeButtonElem, childrenElem } = defineSheetLayout(children);
    const l10n = useLocalization();
    return (jsxs("section", { ref: sheetRef, className: sheetClassNames, tabIndex: -1, "aria-modal": ariaModal, role: "dialog", "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'), style: sheetStyles, ...attributes, children: [jsx("div", { tabIndex: 0, onFocus: trapUpperFocus, "aria-hidden": "true" }), hasCloseButton && (jsxs(EGDSLayoutFlex, { ...layoutProps, children: [jsx(EGDSLayoutFlexItem, { ...closeButtonLayoutProps, children: jsx("div", { children: closeButtonElem }) }), childrenElem && jsx(EGDSLayoutFlexItem, { children: childrenElem })] })), !hasCloseButton && childrenElem, jsx("div", { tabIndex: 0, onFocus: trapLowerFocus, "aria-hidden": "true" })] }));
};
EGDSCenteredSheet.displayName = 'EGDSCenteredSheet';

const getClassNameList$5 = classNames.bind(styles);
const EGDSFullSheet = (props) => {
    const { ariaLabel, autoFocus = true, children, className, returnFocusOnClose = true, isVisible, triggerRef, focusElemRef, theme = 'standard', type = 'full', ...attributes } = props;
    const sheetRef = React.useRef(null);
    const baseClassName = `uitk-${type}-sheet`;
    const sheetClassNames = getClassNameList$5(baseClassName, className, `${baseClassName}-${theme}`);
    // Hooks
    // Set focus when initial mount only
    React.useEffect(() => {
        if (autoFocus && isVisible) {
            setElementFocus(sheetRef, true, focusElemRef);
        }
        // Reset liveannounce so the liveannounce element is not navigable by screenreader.
        isVisible && liveAnnounce('', Level.POLITE);
        isVisible && hasSheetClassName('add');
    }, [autoFocus, isVisible]);
    // "unmount" hook
    React.useEffect(() => () => {
        if (returnFocusOnClose) {
            setFocus(triggerRef);
        }
        hasSheetClassName('remove');
    }, [returnFocusOnClose, triggerRef]);
    const { hasCloseButton, layoutProps, closeButtonLayoutProps, closeButtonElem, childrenElem } = defineSheetLayout(children);
    const l10n = useLocalization();
    return (jsxs("section", { ref: sheetRef, className: sheetClassNames, tabIndex: -1, role: "dialog", "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'), ...attributes, children: [jsx("div", { tabIndex: 0, onFocus: () => trapUpperBoundFocus(sheetRef), "aria-hidden": "true" }), hasCloseButton && (jsxs(EGDSLayoutFlex, { ...layoutProps, children: [jsx(EGDSLayoutFlexItem, { ...closeButtonLayoutProps, children: jsx("div", { children: closeButtonElem }) }), childrenElem && jsx(EGDSLayoutFlexItem, { children: childrenElem })] })), !hasCloseButton && childrenElem, jsx("div", { tabIndex: 0, onFocus: () => trapLowerBoundFocus(sheetRef), "aria-hidden": "true" })] }));
};
EGDSFullSheet.displayName = 'EGDSFullSheet';

const getClassNameList$4 = classNames.bind(styles);
const Sheet = (props) => {
    const { autoFocus = true, children, className, type = 'bottom' } = props;
    const { className: themeClasses, ...themeDataAttributes } = useTheme();
    const themeContext = React.useContext(ThemeContext);
    const isColorThemeContainer = themeContext?.color?.isColorThemeContainer;
    const baseClassName = 'uitk-sheet';
    const sheetClassNames = getClassNameList$4(className, baseClassName, !isColorThemeContainer && themeClasses);
    let Tag;
    switch (type) {
        case 'bottom':
            Tag = EGDSBottomSheet;
            break;
        case 'centered':
            Tag = EGDSCenteredSheet;
            break;
        case 'full':
            Tag = EGDSFullSheet;
            break;
        case 'popover':
            Tag = EGDSPopoverSheet;
            break;
        case 'side':
            Tag = EGDSSideSheet;
            break;
        default:
            Tag = EGDSBottomSheet;
    }
    return (jsx(Tag, { ...props, className: sheetClassNames, autoFocus: autoFocus, ...(!isColorThemeContainer && themeDataAttributes), children: children }));
};
const EGDSSheet = (props) => {
    const l10n = useLocalization();
    return jsx(Sheet, { l10n: l10n, ...props });
};
EGDSSheet.displayName = 'EGDSSheet';

const getClassNameList$3 = classNames.bind(styles);
const EGDSSheetContent = (props) => {
    const { children, className, contentDomRef, fullSheetContentMaxWidth, padded = true } = props;
    const baseClassName = 'uitk-sheet-content';
    const sheetContext = React.useContext(EGDSSheetContext);
    const posStyle = (sheetContext.maxHeight && {
        maxBlockSize: `${MakeEgdsSizingLength(sheetContext.maxHeight)}`,
    }) ||
        undefined;
    const sheetContentClasses = getClassNameList$3(baseClassName, className, {
        [`${baseClassName}-padded`]: padded,
    }, fullSheetContentMaxWidth && `${baseClassName}-${fullSheetContentMaxWidth}`);
    return (jsx("div", { className: sheetContentClasses, ref: contentDomRef, style: posStyle, children: children }));
};
EGDSSheetContent.displayName = 'EGDSSheetContent';

const getClassNameList$2 = classNames.bind(styles);
const EGDSSheetCloseButton = (props) => {
    const { children } = props;
    const closeButtonContainerClass = getClassNameList$2('uitk-sheet-close-button-container');
    return jsx("div", { className: closeButtonContainerClass, children: children });
};
EGDSSheetCloseButton.defaultProps = {
    alignment: 'right',
};
EGDSSheetCloseButton.displayName = 'EGDSSheetCloseButton';

const getClassNameList$1 = classNames.bind(styles);
const EGDSSheetTransition = (props) => {
    const { children, closeCallback, isVisible = false, openCallback } = props;
    const baseClassName = 'uitk-sheet';
    let enterTimeout;
    let exitTimeout;
    // Currently, sheet animation behavior is still being developed by design; as a result, many animation timings are hardcoded.
    // These values come from their respective sheet CSS files until their behavior is fully aligned.
    // See PR #6488 for more details.
    const sheetType = children.props.type;
    switch (sheetType) {
        case 'popover':
            enterTimeout = 180;
            exitTimeout = 60;
            break;
        case 'side':
            enterTimeout = 200;
            exitTimeout = 120;
            break;
        case 'bottom':
            enterTimeout = 275;
            exitTimeout = 275;
            break;
        default:
            // 275ms is the max transition time across sheets for entering and exiting.
            enterTimeout = 275;
            exitTimeout = 275;
    }
    return (jsx(CSSTransition, { classNames: {
            enter: getClassNameList$1(`${baseClassName}-enter`),
            enterActive: getClassNameList$1(`${baseClassName}-enter-active`),
            exit: getClassNameList$1(`${baseClassName}-exit`),
            exitActive: getClassNameList$1(`${baseClassName}-exit-active`),
        }, in: isVisible, unmountOnExit: true, timeout: {
            enter: enterTimeout,
            exit: exitTimeout,
        }, onEntered: openCallback, onExited: closeCallback, children: children }));
};
EGDSSheetTransition.displayName = 'EGDSSheetTransition';

const getClassNameList = classNames.bind(styles);
const EGDSSheetFooter = (props) => {
    const { children, className, ...attributes } = props;
    const baseClass = 'uitk-sheet-footer';
    const cssClassNames = getClassNameList(baseClass, className);
    return (jsx("footer", { ...attributes, className: cssClassNames, children: children }));
};
EGDSSheetFooter.displayName = 'EGDSSheetFooter';

export { EGDSBottomSheet as E, Sheet as S, EGDSPopoverSheet as a, EGDSSideSheet as b, EGDSCenteredSheet as c, EGDSFullSheet as d, EGDSSheet as e, EGDSSheetContent as f, EGDSSheetCloseButton as g, EGDSSheetTransition as h, EGDSSheetFooter as i, EGDSSheetPositionTypes as j, EGDSSideSheetColCounts as k };
//# sourceMappingURL=index-chunk.esm.js.map
